import type {ErrorEvent, EventHint} from "@sentry/react";

/**
 * react-router's `ErrorResponse`: raised for an internal routing failure
 * (`internal: true` — an unmatched URL, a missing loader) and for a 4xx/5xx
 * Response thrown by a loader (`internal: false`).
 *
 * It is a bare class instance, not an `Error`: no `name`, no `message`. Sentry
 * therefore has nothing to title an event with, and each capture path degrades
 * differently — a console capture stringifies it to `[object Object]`, and an
 * exception capture is marked `mechanism.synthetic` (the SDK's marker for
 * anything thrown that isn't an `Error`), which makes Sentry discard the
 * exception type and value and title the group from the top stack frame's
 * function name. That name is minified, so it changes with every build and the
 * one cause mints a fresh issue on each release.
 *
 * Narrowed structurally — mirroring react-router's own `isRouteErrorResponse` —
 * because the value reaching `beforeSend` is either the live class instance
 * (from the exception hint) or the JSON-normalised copy Sentry attaches to the
 * event (from the console arguments).
 */
interface RouteErrorResponse {
  status: number;
  statusText: string;
  internal: boolean;
  data: unknown;
}

/** Exception type stamped on a router error, and the head of its Sentry title. */
const ROUTE_ERROR_TYPE = "RouteErrorResponse";

/** Leading fingerprint component that separates router errors from everything else. */
const ROUTE_ERROR_FINGERPRINT = "router-error-response";

function asRouteErrorResponse(value: unknown): RouteErrorResponse | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const {status, statusText, internal, data} = value as Record<string, unknown>;
  if (typeof status !== "number" || typeof statusText !== "string" || typeof internal !== "boolean") {
    return null;
  }
  if (!("data" in value)) {
    return null;
  }
  return {status, statusText, internal, data};
}

function findRouteErrorResponse(event: ErrorEvent, hint: EventHint): RouteErrorResponse | null {
  // Thrown-exception capture: the ErrorBoundary hands the ErrorResponse straight
  // to captureException, so the original object rides on the hint.
  const fromException = asRouteErrorResponse(hint.originalException);
  if (fromException) {
    return fromException;
  }
  // A captured Error owns the event's identity even when a router error sits
  // alongside it in the console arguments, so its title is left alone.
  if (hint.originalException instanceof Error) {
    return null;
  }
  // Console capture: captureConsole collapses the arguments into the event
  // message and preserves them under `extra.arguments`, normalised to plain JSON.
  const args = event.extra?.arguments;
  if (Array.isArray(args)) {
    for (const arg of args) {
      const fromArgument = asRouteErrorResponse(arg);
      if (fromArgument) {
        return fromArgument;
      }
    }
  }
  return null;
}

/**
 * The build-independent part of an internal router error's message, e.g.
 * `No route matches URL` from `Error: No route matches URL "/tprm/third-parties"`.
 *
 * Only internal errors have a reason derived from them: their messages come from
 * a fixed set of react-router templates, so the result is drawn from a bounded
 * set and is safe to group on. A loader-thrown response carries arbitrary
 * application payload in `data`, which belongs in neither a title nor a
 * fingerprint. The quoted runs hold the varying parts (URL, route id, method)
 * and are dropped for the same reason.
 */
function internalErrorReason({internal, data}: RouteErrorResponse): string | null {
  if (!internal || typeof data !== "string") {
    return null;
  }
  const reason = data
    .replace(/^Error:\s*/, "")
    .replace(/"[^"]*"/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return reason.length > 0 ? reason : null;
}

/** The URL react-router failed to match, quoted after `URL` in its message. */
function offendingPath({data}: RouteErrorResponse): string | null {
  if (typeof data !== "string") {
    return null;
  }
  return /URL "([^"]*)"/.exec(data)?.[1] ?? null;
}

/** Human-readable description of a router error, e.g. `No route matches URL (404 Not Found)`. */
function describeRouteError(response: RouteErrorResponse, reason: string | null): string {
  const status = `${response.status} ${response.statusText}`;
  return reason === null ? status : `${reason} (${status})`;
}

/**
 * A react-router 404 — the router matched no route (an unmatched URL, so
 * `internal`) or a loader threw a 404 `Response`. Both are dead links: stale
 * bookmarks, mistyped URLs, or paths left over from a route rename. The visitor
 * gets the app's own not-found handling and there is no fault to fix, so these
 * are dropped rather than reported.
 */
export function isRouteNotFound(event: ErrorEvent, hint: EventHint): boolean {
  return findRouteErrorResponse(event, hint)?.status === 404;
}

/**
 * Sets the grouping and, for react-router `ErrorResponse`s, a title Sentry can
 * actually render. Every fingerprint ends with the environment so that a group
 * never spans environments; anything that isn't a router error groups by
 * Sentry's default.
 *
 * A router error is fingerprinted on its stable identity — status, reason,
 * whether the router raised it — and deliberately *not* on the offending path.
 * Paths carry UUIDs and are effectively unbounded, so grouping on one would mint
 * an issue per URL. The path is a tag instead, which keeps the distinct causes
 * behind a shared error (a malformed generated link, a stale bookmark) separable
 * inside the one group.
 */
export function applyEventGrouping(event: ErrorEvent, hint: EventHint, environment: string): void {
  const response = findRouteErrorResponse(event, hint);
  if (response === null) {
    event.fingerprint = ["{{ default }}", environment];
    return;
  }

  const reason = internalErrorReason(response);
  const description = describeRouteError(response, reason);

  event.fingerprint = [
    ROUTE_ERROR_FINGERPRINT,
    response.internal ? "internal" : "response",
    String(response.status),
    reason ?? response.statusText,
    environment,
  ];

  // Sentry titles an exception event from the last value in the list, and a
  // message event from its message; a router error arrives as one or the other.
  const values = event.exception?.values ?? [];
  const primary = values[values.length - 1];
  if (primary) {
    primary.type = ROUTE_ERROR_TYPE;
    primary.value = description;
    if (primary.mechanism?.synthetic) {
      // Sentry only reads an exception's type and value when the mechanism is
      // not synthetic; the pair set above is the description of the error, so it
      // has to be the one Sentry titles the group with.
      primary.mechanism = {...primary.mechanism, synthetic: false};
    }
  }
  if (event.message !== undefined) {
    event.message = `${ROUTE_ERROR_TYPE}: ${description}`;
  }

  const path = offendingPath(response);
  event.tags = {...event.tags, "router.status": response.status, ...(path === null ? {} : {"router.path": path})};
  event.contexts = {
    ...event.contexts,
    router: {
      status: response.status,
      statusText: response.statusText,
      internal: response.internal,
      data: response.data,
    },
  };
}
